import { Icon } from '@/shared/assets/icons';
import { cn } from '@/shared/lib/cn';
import { Loader } from '@/shared/ui/loader';
import { Typography, type TypographyVariant } from '@/shared/ui/typography';
import {
  forwardRef,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type ComponentPropsWithoutRef,
} from 'react';

const sizeClasses: Record<AvatarSize, string> = {
  small: 'w-10 h-10',
  large: 'w-18 h-18',
};

const fallbackIconSizes: Record<AvatarSize, number> = {
  small: 20,
  large: 32,
};

const initialsVariants: Record<AvatarSize, TypographyVariant> = {
  small: 'body',
  large: 'h3',
};

type AvatarSize = 'small' | 'large';

type AvatarProps = {
  src: string;
  size?: AvatarSize;
  /** Shown instead of the generic user icon when there is no image. */
  initials?: string;
  /** Covers the avatar with a spinner while a new photo is being uploaded. */
  isUploading?: boolean;
  imgProps?: Omit<ComponentProps<'img'>, 'src' | 'alt'>;
  onEdit?: (file: File) => void;
} & ComponentPropsWithoutRef<'div'>;

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      size = 'small',
      src,
      initials,
      isUploading = false,
      imgProps,
      className,
      onEdit,
      ...props
    },
    ref
  ) => {
    // Remember *which* src failed rather than a bare flag: a boolean stayed true
    // after a new photo was uploaded, so the avatar kept showing the fallback
    // until the page was reloaded.
    const [erroredSrc, setErroredSrc] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isFallback = !src || erroredSrc === src;
    const showInitials = isFallback && !!initials;

    const handleAvatarEdit = () => {
      if (onEdit && !isUploading) {
        fileInputRef.current?.click();
      }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!onEdit) return;

      const file = e.target.files?.[0];
      if (file) {
        onEdit(file);
      }
    };

    return (
      <div
        ref={ref}
        role={onEdit ? 'button' : 'img'}
        tabIndex={onEdit ? 0 : undefined}
        className={cn(
          sizeClasses[size],
          'group relative flex shrink-0 items-center justify-center rounded-full',
          isFallback && 'bg-primary-8',
          isFallback && !showInitials && 'p-2.5',
          className
        )}
        onClick={handleAvatarEdit}
        {...props}
      >
        {showInitials ? (
          <Typography
            as="span"
            weight="medium"
            variant={initialsVariants[size]}
            className={cn(
              'text-primary-inverse select-none',
              onEdit && 'group-hover:opacity-0'
            )}
          >
            {initials}
          </Typography>
        ) : isFallback ? (
          <Icon
            name="user"
            size={fallbackIconSizes[size]}
            className={cn(
              'text-primary-inverse',
              onEdit && 'group-hover:opacity-0'
            )}
          />
        ) : (
          <img
            {...imgProps}
            alt="User Avatar"
            src={src}
            className={cn(
              sizeClasses[size],
              'rounded-full object-cover',
              imgProps?.className
            )}
            onError={() => setErroredSrc(src)}
          />
        )}

        {isUploading && (
          <div
            role="status"
            aria-label="Uploading photo"
            className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50"
          >
            <Loader
              currentColor
              className="text-primary-inverse"
              size={fallbackIconSizes[size]}
            />
          </div>
        )}

        {onEdit && !isUploading && (
          <>
            <button
              type="button"
              className={cn(
                'absolute inset-0 flex items-center justify-center rounded-full bg-black/40',
                'opacity-0 transition-opacity group-hover:opacity-100'
              )}
            >
              <Icon
                name="edit-pencil"
                className="text-white"
                size={fallbackIconSizes[size]}
              />
            </button>

            <input
              type="file"
              className="hidden"
              aria-label="Change Avatar"
              accept="image/png,image/jpeg,image/jpg"
              value={''}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </>
        )}
      </div>
    );
  }
);

Avatar.displayName = 'UI/Avatar';
